/**
 * LOGOS v5.0 — Emergence Metrics (Layer 9)
 * SRS-EXPVAL-001: Emergence Theory Computations
 * 
 * This module is ADDITIVE — it does NOT modify any existing core functions.
 * It provides 6 new computational functions (#49-54) for emergence theory
 * visualization and analysis, distinct from the protocol statistics in protocol-engine.ts.
 * 
 * v5.1: Updated to use Hierarchical Lambda (Signal × Channel).
 * E(x) now detects changes in ALL 28 dimensions via the product rule:
 *   Λ̇ = Λ̇_signal · Φ + Λ_signal · Φ̇
 * 
 * Functions: 6 new (functions #49-54 in the v5.0 inventory)
 */

import { computeChannelQuality, GAMMA_CHANNEL_ATTENUATION } from './logos';
import type { ConsciousnessState } from '../types';

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

export interface EmergenceIndexResult {
  value: number;
  domainLambdaDots: Record<string, number>;
  variance: number;
  interpretation: string;
  coherence: number;
  magnitudeFactor: number;
}

export interface MultiScaleLambdaResult {
  global: number;
  byDomain: Record<string, number>;
  strongest: string;
  weakest: string;
  spread: number;
}

export interface CriticalityAlert {
  active: boolean;
  severity: 'none' | 'watch' | 'warning' | 'critical';
  culpritDomain: string | null;
  culpritValue: number;
  coherenceAcceleration: number;
  message: string;
  timeToCollapse: number | null;
}

export interface EnergyLandscapePoint {
  lambda: number;
  entropy: number;
  attractorForce: number;
  nearestAttractor: 'A_plus' | 'A_minus' | 'separatrix';
}

export interface CoherenceMatrixResult {
  matrix: number[][];
  domainLabels: string[];
  strongestCoupling: [string, string, number];
  weakestCoupling: [string, string, number];
}

// ═══════════════════════════════════════════════════════════════
// DOMAIN DEFINITIONS — 7 domains × 4 dims each = 28 total
// Must match DOMAINS in LogosHumano.jsx EXACTLY
// ═══════════════════════════════════════════════════════════════

export const DOMAIN_DEFINITIONS: Record<string, {
  label: string;
  dims: string[];
  color: string;
}> = {
  physical: {
    label: 'Cuerpo',
    dims: ['sleep', 'nutrition', 'exercise', 'energy'],
    color: '#10B981',
  },
  emotional: {
    label: 'Emociones',
    dims: ['peace', 'joy', 'gratitude', 'love'],
    color: '#F59E0B',
  },
  mental: {
    label: 'Mente',
    dims: ['clarity', 'focus', 'creativity', 'wisdom'],
    color: '#6366F1',
  },
  spiritual: {
    label: 'Espíritu',
    dims: ['faith', 'meditation', 'service', 'presence'],
    color: '#A855F7',
  },
  relational: {
    label: 'Relaciones',
    dims: ['family', 'friendship', 'community', 'compassion'],
    color: '#EC4899',
  },
  purpose: {
    label: 'Propósito',
    dims: ['meaning', 'mission', 'contribution', 'legacy'],
    color: '#0EA5E9',
  },
  alimento: {
    label: 'Alimento Sagrado',
    dims: ['nourishment', 'taste_presence', 'food_harmony', 'gut_resonance'],
    color: '#84cc16',
  },
};

const DOMAIN_KEYS = Object.keys(DOMAIN_DEFINITIONS);

// ═══════════════════════════════════════════════════════════════
// LAMBDA SIGNAL WEIGHTS — The 12 dimensions of Λ_signal
// Extracted from LOGOS_COMPONENTS in logos.ts
// These compute Λ_signal(x) = Σ(w_i × s_i), Sum = 1.00 ✓
//
// Under Hierarchical Lambda (Option C):
//   Λ(x) = Λ_signal(x) × Φ_channel(x)
// The 16 non-signal dimensions modulate Λ via Φ_channel.
// ALL 28 dimensions now contribute to Λ (Corollary 1.1 restored).
// ═══════════════════════════════════════════════════════════════

export const LAMBDA_WEIGHTS: Record<string, number> = {
  // ── Reception (∑=0.43) ──
  faith: 0.17,       // Apertura del canal
  meditation: 0.13,  // Sintonización de frecuencia
  presence: 0.13,    // Eliminación de ruido temporal
  // ── Action (∑=0.29) ──
  service: 0.11,     // Señal convertida en acción
  love: 0.10,        // La frecuencia más pura
  compassion: 0.08,  // Empatía activa
  // ── Decoding (∑=0.18) ──
  wisdom: 0.08,      // Discernimiento profundo
  meaning: 0.06,     // Significado percibido
  clarity: 0.04,     // Nitidez de interpretación
  // ── Sacred Nourishment (∑=0.08) ──
  taste_presence: 0.05, // Consciencia plena al comer
  food_harmony: 0.03,   // Balance nutricional consciente
  // ── Gratitude (∑=0.02) ──
  gratitude: 0.02,   // Acknowledgment de la fuente
};
// Sum: 0.17+0.13+0.13 + 0.11+0.10+0.08 + 0.08+0.06+0.04 + 0.05+0.03 + 0.02 = 1.00 ✓
//
// Domain Λ coverage:
//   spiritual: faith+meditation+presence+service = 0.54 (ALL 4 dims)
//   emotional: love+gratitude = 0.12 (2 of 4; peace, joy NOT in Λ)
//   mental:    wisdom+clarity = 0.12 (2 of 4; focus, creativity NOT in Λ)
//   relational:compassion = 0.08 (1 of 4; family, friendship, community NOT in Λ)
//   alimento:  taste_presence+food_harmony = 0.08 (2 of 4; nourishment, gut_resonance NOT in Λ)
//   purpose:   meaning = 0.06 (1 of 4; mission, contribution, legacy NOT in Λ)
//   physical:  NONE = 0.00 (0 of 4 — affects V, S, not Λ)

// ═══════════════════════════════════════════════════════════════
// FUNCTION #49: computeEmergenceIndex — EVOLUTIONARY
//
// E(x) is an evolutionary metric: it accumulates history via EMA.
// It NEVER resets — it uses the last E(x) as starting point and
// evolves based on new domain dynamics.
//
// Instantaneous components:
//   1. Coherence = 1 - σ²(Λ̇_d)/σ²_max  (synchrony of domain changes)
//   2. Direction  = positive Λ̇ mean → E rises; negative → E falls
//   3. Magnitude  = tanh(k × Σ|Λ̇_d|) determines EMA blend factor α
//
// Evolutionary EMA:
//   When change detected (magnitude > 0):
//     E_new = α × E_instantaneous + (1-α) × E_prev
//   When no change (stasis):
//     E_new = E_prev × temporal_decay  (gentle, 72h half-life)
//
// The metric carries forward all prior state — truly evolutionary.
// ═══════════════════════════════════════════════════════════════

const EMERGENCE_INITIAL_SEED = 0.50;

export function computeEmergenceIndex(
  currentState: Record<string, number>,
  previousState: Record<string, number> | null,
  previousEmergenceValue?: number | null,
  lastCheckinTimestamp?: number | null
): EmergenceIndexResult {
  const E_prev = (typeof previousEmergenceValue === 'number' && isFinite(previousEmergenceValue))
    ? previousEmergenceValue
    : EMERGENCE_INITIAL_SEED;

  // ── No previous state: preserve last E(x) with gentle temporal decay ──
  if (!previousState) {
    let decayed = E_prev;
    if (lastCheckinTimestamp) {
      const hoursElapsed = (Date.now() - lastCheckinTimestamp) / (1000 * 60 * 60);
      const TAU_HOURS = 72; // 72h half-life — very gentle
      const decay = Math.exp(-0.693 * hoursElapsed / TAU_HOURS);
      // Decay pulls toward 0.5 (neutral), not toward 0
      decayed = 0.5 + (E_prev - 0.5) * Math.max(0.2, decay);
    }
    return {
      value: +decayed.toFixed(3),
      domainLambdaDots: {},
      variance: 0,
      interpretation: interpretEmergence(decayed, 0),
      coherence: 1,
      magnitudeFactor: 0,
    };
  }

  // ── Compute Λ̇ per domain using Hierarchical Lambda (Signal × Channel) ──
  // Under Option C: Λ = Λ_signal · Φ_channel
  // By the product rule: Λ̇ = Λ̇_signal · Φ + Λ_signal · Φ̇
  // This means ALL 28 dims now contribute to Λ̇:
  //   - Signal dims (12) contribute via Λ̇_signal · Φ
  //   - Channel dims (16) contribute via Λ_signal · Φ̇
  // Physical domain is no longer structurally zero.

  // Compute global Λ_signal and Φ for both states
  let lambdaSignalNow = 0;
  let lambdaSignalPrev = 0;
  Object.entries(LAMBDA_WEIGHTS).forEach(([dim, w]) => {
    lambdaSignalNow += w * (currentState[dim] ?? 0.5);
    lambdaSignalPrev += w * (previousState[dim] ?? 0.5);
  });

  const phiNow = computeChannelQuality(
    currentState as unknown as ConsciousnessState
  ).phi;
  const phiPrev = computeChannelQuality(
    previousState as unknown as ConsciousnessState
  ).phi;

  // Hierarchical Λ for both states
  const lambdaNow = lambdaSignalNow * phiNow;
  const lambdaPrev = lambdaSignalPrev * phiPrev;

  const domainLambdaDots: Record<string, number> = {};

  DOMAIN_KEYS.forEach((domKey) => {
    const dims = DOMAIN_DEFINITIONS[domKey].dims;

    // Signal contribution: change in Λ_signal for this domain's dims × Φ_now
    let signalContribNow = 0;
    let signalContribPrev = 0;
    dims.forEach((dim) => {
      const w = LAMBDA_WEIGHTS[dim] || 0;
      signalContribNow += w * (currentState[dim] ?? 0.5);
      signalContribPrev += w * (previousState[dim] ?? 0.5);
    });
    const signalDot = (signalContribNow - signalContribPrev) * phiNow;

    // Channel contribution: change in domain's channel dims × Λ_signal_prev
    // Each domain may contain channel dims (dims NOT in LAMBDA_WEIGHTS)
    let channelAvgNow = 0;
    let channelAvgPrev = 0;
    let channelDimCount = 0;
    dims.forEach((dim) => {
      if (!LAMBDA_WEIGHTS[dim]) {
        channelAvgNow += (currentState[dim] ?? 0.5);
        channelAvgPrev += (previousState[dim] ?? 0.5);
        channelDimCount++;
      }
    });

    let channelDot = 0;
    if (channelDimCount > 0) {
      // Approximate ∂Φ/∂domain contribution
      const avgDeltaChannel = (channelAvgNow - channelAvgPrev) / channelDimCount;
      // ∂Λ/∂channel_dim = Λ_signal · γ · α_k / n_k
      // Simplified: use total magnitude of channel change scaled by Λ_signal and γ
      channelDot = lambdaSignalNow * GAMMA_CHANNEL_ATTENUATION * avgDeltaChannel * (channelDimCount / 16);
    }

    domainLambdaDots[domKey] = signalDot + channelDot;
  });

  // ── Variance of Λ̇ across ALL domains ──
  // Under Hierarchical Lambda, all 7 domains can contribute to Λ̇.
  // Physical domain now has non-zero Λ̇ via channel modulation.
  const activeDots = DOMAIN_KEYS.map(dk => domainLambdaDots[dk]);

  const meanDot = activeDots.length > 0
    ? activeDots.reduce((s, v) => s + v, 0) / activeDots.length
    : 0;
  const variance = activeDots.length > 0
    ? activeDots.reduce((s, v) => s + Math.pow(v - meanDot, 2), 0) / activeDots.length
    : 0;

  const maxVariance = 0.04; // empirically calibrated for 7 active domains (all contribute under Hierarchical Λ)

  // Component 1: Coherence (synchrony) — 0 to 1
  const coherence = Math.max(0, Math.min(1, 1 - variance / maxVariance));

  // Component 2: Magnitude — total absolute change across active domains
  const totalMagnitude = activeDots.reduce((s: number, v: number) => s + Math.abs(v), 0);
  const magnitudeK = 50; // tanh(50 × 0.02) ≈ 0.76 for moderate change
  const magnitudeFactor = Math.tanh(magnitudeK * totalMagnitude);

  // Component 3: Direction — positive mean Λ̇ pushes E up, negative pushes down
  // Instantaneous E captures the "ideal" emergence from this transition
  const directionBonus = meanDot > 0 ? 1.0 + Math.min(0.15, meanDot * 5) : 1.0;
  const directionPenalty = meanDot < 0 ? 1.0 + Math.max(-0.15, meanDot * 5) : 1.0;
  const E_instantaneous = Math.max(0, Math.min(1,
    coherence * directionBonus * directionPenalty
  ));

  // ── Temporal decay for the previous E(x) — gentle 72h half-life ──
  let E_prev_decayed = E_prev;
  if (lastCheckinTimestamp) {
    const hoursElapsed = (Date.now() - lastCheckinTimestamp) / (1000 * 60 * 60);
    const TAU_HOURS = 72;
    const decay = Math.exp(-0.693 * hoursElapsed / TAU_HOURS);
    // Decay pulls toward neutral 0.5, not toward 0
    E_prev_decayed = 0.5 + (E_prev - 0.5) * Math.max(0.2, decay);
  }

  // ── EMA blend: α = magnitudeFactor ──
  // High magnitude → trust new instantaneous E more
  // Low magnitude (stasis) → preserve previous E (α ≈ 0)
  const alpha = magnitudeFactor;
  const E = Math.max(0, Math.min(1,
    alpha * E_instantaneous + (1 - alpha) * E_prev_decayed
  ));

  return {
    value: +E.toFixed(3),
    domainLambdaDots,
    variance,
    interpretation: interpretEmergence(E, magnitudeFactor),
    coherence,
    magnitudeFactor,
  };
}

function interpretEmergence(E: number, magnitude: number): string {
  if (magnitude < 0.05) {
    if (E > 0.65) return 'Resonancia sostenida';
    if (E > 0.45) return 'Estable';
    return 'Latente';
  }
  if (E > 0.75) return 'Auto-organizándose';
  if (E > 0.55) return 'Emergiendo';
  if (E > 0.40) return 'Fluctuando';
  return 'Divergiendo';
}

// ═══════════════════════════════════════════════════════════════
// FUNCTION #50: computeMultiScaleLambda
// Λ_d = contribution of domain d to global Λ
// Makes "where is your signal strong/weak" actionable
//
// Uses LAMBDA_WEIGHTS (canonical 12 dims from logos.ts) for both
// global Λ and per-domain decomposition. Single source of truth.
// ═══════════════════════════════════════════════════════════════

export function computeMultiScaleLambda(
  state: Record<string, number>
): MultiScaleLambdaResult {
  const byDomain: Record<string, number> = {};
  let globalLambda = 0;

  // Decompose Λ into domain contributions using LAMBDA_WEIGHTS (canonical 12 dims)
  // Domains with no Λ-contributing dims (physical) get byDomain = 0
  DOMAIN_KEYS.forEach((domKey) => {
    const dims = DOMAIN_DEFINITIONS[domKey].dims;
    let domainContrib = 0;
    let domainMaxContrib = 0;

    dims.forEach((dim) => {
      const w = LAMBDA_WEIGHTS[dim] || 0;
      domainContrib += w * (state[dim] ?? 0.5);
      domainMaxContrib += w;
    });

    // Normalize to [0,1] within domain's Λ range
    // Physical: 0/0 → 0 (no Λ contribution, correct)
    byDomain[domKey] = domainMaxContrib > 0
      ? domainContrib / domainMaxContrib
      : 0;

    globalLambda += domainContrib;
  });

  // Find strongest and weakest
  let strongest = DOMAIN_KEYS[0];
  let weakest = DOMAIN_KEYS[0];
  DOMAIN_KEYS.forEach((k) => {
    if (byDomain[k] > byDomain[strongest]) strongest = k;
    if (byDomain[k] < byDomain[weakest]) weakest = k;
  });

  const values = Object.values(byDomain);
  const spread = Math.max(...values) - Math.min(...values);

  return { global: Math.max(0, Math.min(1, globalLambda)), byDomain, strongest, weakest, spread };
}

// ═══════════════════════════════════════════════════════════════
// FUNCTION #51: computeCriticalityAlert
// Detects PRE-COLLAPSE based on coherence acceleration (C̈)
// Theorem 5: harmonic mean transitions of phase when any D_k→0
// ═══════════════════════════════════════════════════════════════

export function computeCriticalityAlert(
  currentState: Record<string, number>,
  previousState: Record<string, number> | null,
  prevPrevState: Record<string, number> | null,
  coherenceCurrent: number,
  coherencePrevious: number | null,
  coherencePrevPrev: number | null
): CriticalityAlert {
  const noAlert: CriticalityAlert = {
    active: false, severity: 'none', culpritDomain: null, culpritValue: 0,
    coherenceAcceleration: 0, message: '', timeToCollapse: null,
  };

  // Need at least 2 previous states for C̈
  if (coherencePrevious === null) return noAlert;

  // First derivative: Ċ
  const cDot = coherenceCurrent - coherencePrevious;

  // Second derivative: C̈ (if we have 3 data points)
  let cDotDot = 0;
  if (coherencePrevPrev !== null) {
    const cDotPrev = coherencePrevious - coherencePrevPrev;
    cDotDot = cDot - cDotPrev;
  }

  // Find the culprit domain — the one with lowest average (1/D_k largest in harmonic)
  let minDomain = '';
  let minValue = 1.0;

  DOMAIN_KEYS.forEach((domKey) => {
    const dims = DOMAIN_DEFINITIONS[domKey].dims;
    const avg = dims.reduce((s, d) => s + (currentState[d] || 0.5), 0) / dims.length;
    if (avg < minValue) {
      minValue = avg;
      minDomain = domKey;
    }
  });

  const domainLabel = DOMAIN_DEFINITIONS[minDomain as keyof typeof DOMAIN_DEFINITIONS]?.label || minDomain;

  // Decision logic
  // Critical: C̈ < -0.05 AND C < 0.40 AND some domain < 0.25
  if (cDotDot < -0.05 && coherenceCurrent < 0.40 && minValue < 0.25) {
    const estimatedDays = Math.max(1, Math.round(coherenceCurrent / Math.abs(cDot || 0.01)));
    return {
      active: true,
      severity: 'critical',
      culpritDomain: minDomain,
      culpritValue: minValue,
      coherenceAcceleration: cDotDot,
      message: `⚠️ FASE CRÍTICA: Dominio ${domainLabel} (${(minValue * 100).toFixed(0)}%) está colapsando tu coherencia. Riesgo de caída a A⁻ en ~${estimatedDays} días.`,
      timeToCollapse: estimatedDays,
    };
  }

  // Warning: C̈ < -0.02 AND C < 0.50
  if (cDotDot < -0.02 && coherenceCurrent < 0.50) {
    return {
      active: true,
      severity: 'warning',
      culpritDomain: minDomain,
      culpritValue: minValue,
      coherenceAcceleration: cDotDot,
      message: `⚡ Coherencia decayendo aceleradamente. Dominio ${domainLabel} (${(minValue * 100).toFixed(0)}%) necesita atención.`,
      timeToCollapse: null,
    };
  }

  // Watch: Ċ < -0.03 (declining, not yet accelerating)
  if (cDot < -0.03) {
    return {
      active: true,
      severity: 'watch',
      culpritDomain: minDomain,
      culpritValue: minValue,
      coherenceAcceleration: cDotDot,
      message: `👀 Coherencia descendiendo. Observa tu dominio ${domainLabel}.`,
      timeToCollapse: null,
    };
  }

  return noAlert;
}

// ═══════════════════════════════════════════════════════════════
// FUNCTION #52: computeEnergyLandscape
// 2D landscape: Λ vs S, with attractor forces
// Based on Watson framework + Theorem 3 (Teleonomy)
// ═══════════════════════════════════════════════════════════════

export function computeEnergyLandscape(
  lambda: number,
  entropy: number
): EnergyLandscapePoint {
  // Attractor positions
  const A_PLUS = { lambda: 0.85, entropy: 0.10 };  // Logos alignment
  const A_MINUS = { lambda: 0.10, entropy: 0.85 };  // Entropic spiral
  const SEPARATRIX_LAMBDA = 0.35; // Critical threshold

  // Distance to each attractor
  const distPlus = Math.sqrt(
    Math.pow(lambda - A_PLUS.lambda, 2) + Math.pow(entropy - A_PLUS.entropy, 2)
  );
  const distMinus = Math.sqrt(
    Math.pow(lambda - A_MINUS.lambda, 2) + Math.pow(entropy - A_MINUS.entropy, 2)
  );

  // Force magnitude (inverse square, clamped)
  const forcePlus = 1 / Math.max(0.1, distPlus * distPlus);
  const forceMinus = 1 / Math.max(0.1, distMinus * distMinus);

  let nearestAttractor: 'A_plus' | 'A_minus' | 'separatrix';
  if (Math.abs(lambda - SEPARATRIX_LAMBDA) < 0.08) {
    nearestAttractor = 'separatrix';
  } else if (distPlus < distMinus) {
    nearestAttractor = 'A_plus';
  } else {
    nearestAttractor = 'A_minus';
  }

  return {
    lambda,
    entropy,
    attractorForce: nearestAttractor === 'A_plus' ? forcePlus : forceMinus,
    nearestAttractor,
  };
}

// ═══════════════════════════════════════════════════════════════
// FUNCTION #53: computeCoherenceMatrix
// 7×7 inter-domain correlation matrix
// Reveals hidden couplings between life domains
// ═══════════════════════════════════════════════════════════════

export function computeCoherenceMatrix(
  state: Record<string, number>
): CoherenceMatrixResult {
  const domainLabels = DOMAIN_KEYS.map(
    (k) => DOMAIN_DEFINITIONS[k].label
  );

  // Compute domain averages
  const domainAvgs: number[] = DOMAIN_KEYS.map((domKey) => {
    const dims = DOMAIN_DEFINITIONS[domKey].dims;
    return dims.reduce((s, d) => s + (state[d] || 0.5), 0) / dims.length;
  });

  // Compute correlation matrix
  // For a single state vector, we use co-deviation from 0.5 as proxy
  // Full correlation requires time-series data (sessions history)
  // This is the INSTANTANEOUS coherence approximation
  const n = DOMAIN_KEYS.length;
  const matrix: number[][] = [];

  for (let i = 0; i < n; i++) {
    matrix[i] = [];
    for (let j = 0; j < n; j++) {
      if (i === j) {
        matrix[i][j] = 1.0;
      } else {
        // Coherence proxy: how similarly are two domains deviating from center?
        const devI = domainAvgs[i] - 0.5;
        const devJ = domainAvgs[j] - 0.5;
        // Same direction = positive coherence, opposite = negative
        const product = devI * devJ;
        // Normalize by magnitude
        const magI = Math.abs(devI) || 0.01;
        const magJ = Math.abs(devJ) || 0.01;
        matrix[i][j] = Math.max(-1, Math.min(1, product / (magI * magJ) * Math.min(magI, magJ) * 4));
      }
    }
  }

  // Find strongest and weakest coupling (off-diagonal)
  let strongest: [string, string, number] = [domainLabels[0], domainLabels[1], -2];
  let weakest: [string, string, number] = [domainLabels[0], domainLabels[1], 2];

  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (matrix[i][j] > strongest[2]) {
        strongest = [domainLabels[i], domainLabels[j], matrix[i][j]];
      }
      if (matrix[i][j] < weakest[2]) {
        weakest = [domainLabels[i], domainLabels[j], matrix[i][j]];
      }
    }
  }

  return { matrix, domainLabels, strongestCoupling: strongest, weakestCoupling: weakest };
}

// ═══════════════════════════════════════════════════════════════
// FUNCTION #54: computeShannonSNR (Enhanced)
// Full Shannon channel quality metric with gauge interpretation
// Extends the basic computeSNR from protocol-engine.ts
// ═══════════════════════════════════════════════════════════════

export function computeShannonSNR(
  lambda: number,
  entropy: number,
  hoffmanBiases: number[] | null // array of bias magnitudes, if available
): { snr: number; quality: string; color: string; gaugePercent: number } {
  const signal = Math.max(0.001, lambda);
  const noise = Math.max(0.001, entropy);

  // Add Hoffman bias noise if available
  const biasNoise = hoffmanBiases
    ? hoffmanBiases.reduce((s, b) => s + Math.abs(b), 0) / hoffmanBiases.length * 0.3
    : 0;

  const totalNoise = noise + biasNoise;
  const snr = Math.log2(signal / totalNoise);

  // Interpretation
  let quality: string;
  let color: string;
  let gaugePercent: number;

  if (snr > 2.0) {
    quality = 'Señal Clara';
    color = '#10B981';
    gaugePercent = Math.min(100, 50 + snr * 15);
  } else if (snr > 0.5) {
    quality = 'Señal Moderada';
    color = '#F59E0B';
    gaugePercent = 30 + snr * 20;
  } else if (snr > -0.5) {
    quality = 'Señal Débil';
    color = '#F97316';
    gaugePercent = 15 + (snr + 0.5) * 15;
  } else {
    quality = 'Canal Ruidoso';
    color = '#EF4444';
    gaugePercent = Math.max(3, 10 + snr * 5);
  }

  return { snr, quality, color, gaugePercent: Math.max(3, Math.min(100, gaugePercent)) };
}
